import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  CheckCircle,
  Clock,
  Phone,
  Video,
  MessageSquare,
  ArrowLeft,
  Calendar as CalendarIcon,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultationBooking = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    consultationType: '',
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    carType: '',
    dealershipLocation: '',
    message: ''
  });

  const consultationTypes = [
    {
      id: 'quick',
      name: 'Quick Call',
      price: '$14.99',
      duration: '15 minutes',
      icon: Phone,
      description: 'Perfect for buyers who need immediate expert guidance before walking into a dealership or facing a high-pressure sales situation',
      features: [
        'Instant answers to your urgent car-buying questions',
        'Red flags to watch for at your specific dealership',
        'Quick negotiation tactics you can use immediately',
        'Written email summary of our conversation',
        'Peace of mind knowing you\'re not getting ripped off'
      ]
    },
    {
      id: 'standard',
      name: 'Strategy Session',
      price: '$59.99',
      duration: '45 minutes',
      icon: Video,
      popular: true,
      description: 'Our most popular package - Get 45 minutes of concentrated expertise that could save you thousands. Walk into any dealership armed with insider knowledge and a battle-tested game plan',
      features: [
        'Custom negotiation strategy designed for YOUR situation',
        'Deep-dive market analysis: know exactly what your target car should cost',
        'Trade-in maximization techniques (typically saves $800-$2,000 alone)',
        'Financing defense tactics to avoid dealer markup tricks',
        'Word-for-word scripts for every stage of negotiation',
        'Post-call email with your personalized action plan',
        '7-day email support for follow-up questions'
      ]
    },
    {
      id: 'premium',
      name: 'Full Support Package',
      price: '$249.99',
      duration: '2 hours + ongoing',
      icon: MessageSquare,
      description: 'The ultimate VIP experience - We become your personal car-buying coach from start to finish. From choosing the right vehicle to walking out with the keys, we\'re with you every step. This is like having a dealership insider on YOUR side',
      features: [
        'Everything in Strategy Session PLUS exclusive benefits',
        'Vehicle selection consultation - make sure you\'re buying the RIGHT car',
        'Real-time text support while you\'re AT the dealership',
        'In-person dealership assistance available (select locations)',
        'Line-by-line contract review before you sign anything',
        'F&I office defense coaching (where dealers make their real money)',
        'Post-purchase support if issues arise',
        '30-day follow-up call to ensure satisfaction',
        'Guaranteed to save you at least 10x your investment'
      ]
    }
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM',
    '3:00 PM', '4:00 PM', '5:00 PM',
    '6:00 PM', '7:00 PM'
  ];

  const handleTypeSelect = (type) => {
    setFormData({ ...formData, consultationType: type });
    setStep(2);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.preferredDate || !formData.preferredTime) {
      toast.error('Please select a date and time');
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedType = consultationTypes.find(t => t.id === formData.consultationType);
      
      const response = await axios.post(`${API}/consultation/request`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        preferred_date: `${formData.preferredDate} at ${formData.preferredTime}`,
        message: `Consultation Type: ${selectedType?.name}\nCar Type: ${formData.carType}\nDealership Location: ${formData.dealershipLocation}\n\nAdditional Notes: ${formData.message}`
      });

      toast.success('Consultation booked! Check your email for confirmation.');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book consultation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Button
            onClick={() => step === 1 ? navigate('/') : setStep(1)}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 1 ? 'Back to Home' : 'Back to Packages'}
          </Button>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Book Your 1-on-1 Consultation
          </h1>
          <p className="text-xl text-gray-600">
            Get expert guidance to save thousands on your next car purchase
          </p>
        </div>

        {/* Step 1: Choose Consultation Type */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
              Choose Your Consultation Package
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {consultationTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <Card
                    key={type.id}
                    className={`relative cursor-pointer transition-all hover:shadow-xl ${
                      type.popular ? 'border-2 border-orange-500' : 'border-2 hover:border-orange-500'
                    }`}
                    onClick={() => handleTypeSelect(type.id)}
                  >
                    {type.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-orange-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                          MOST POPULAR
                        </span>
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon className="w-8 h-8 text-orange-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{type.name}</h3>
                        <div className="text-3xl font-bold text-orange-600 mb-1">{type.price}</div>
                        <div className="flex items-center justify-center text-gray-600 text-sm">
                          <Clock className="w-4 h-4 mr-1" />
                          {type.duration}
                        </div>
                      </div>
                      <p className="text-gray-600 text-center mb-6">{type.description}</p>
                      <ul className="space-y-3 mb-6">
                        {type.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-gray-700">
                            <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button className="w-full bg-orange-600 hover:bg-orange-700">
                        Select Package
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Booking Form */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {consultationTypes.find(t => t.id === formData.consultationType)?.name}
                  </h2>
                  <p className="text-gray-600">
                    Fill out the form below and we'll get back to you within 24 hours
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Your Information</h3>
                    
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Schedule Your Call</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="preferredDate">Preferred Date *</Label>
                        <Input
                          id="preferredDate"
                          name="preferredDate"
                          type="date"
                          value={formData.preferredDate}
                          onChange={handleInputChange}
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="preferredTime">Preferred Time *</Label>
                        <select
                          id="preferredTime"
                          name="preferredTime"
                          value={formData.preferredTime}
                          onChange={handleInputChange}
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          required
                        >
                          <option value="">Select a time</option>
                          {timeSlots.map((slot) => (
                            <option key={slot} value={slot}>{slot}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Car Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">About Your Purchase</h3>
                    
                    <div>
                      <Label htmlFor="carType">What car are you looking to buy?</Label>
                      <Input
                        id="carType"
                        name="carType"
                        type="text"
                        value={formData.carType}
                        onChange={handleInputChange}
                        placeholder="e.g., 2024 Honda Accord, Used Toyota Camry"
                      />
                    </div>

                    <div>
                      <Label htmlFor="dealershipLocation">Dealership Location (if known)</Label>
                      <Input
                        id="dealershipLocation"
                        name="dealershipLocation"
                        type="text"
                        value={formData.dealershipLocation}
                        onChange={handleInputChange}
                        placeholder="City, State"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Additional Notes or Questions</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your situation, timeline, or any specific concerns..."
                        rows={4}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-orange-600 hover:bg-orange-700 h-12 text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        <CalendarIcon className="mr-2 w-5 h-5" />
                        Confirm Booking
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    You'll receive a confirmation email with next steps
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultationBooking;